import React from "react";
import { Button } from "@/components/UI/button";
import { cn } from "@/lib/utils";

export interface HeaderNavItem {
  label: string;
  href?: string;
  onClick?: () => void;
  active?: boolean;
}

export interface HeaderAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface NavigationHeaderProps {
  brandLabel?: string;
  navItems?: HeaderNavItem[];
  primaryAction?: HeaderAction;
  secondaryAction?: HeaderAction;
  className?: string;
}

const defaultNavItems: HeaderNavItem[] = [
  { label: "Home", href: "#" },
  { label: "How it works", href: "#" },
  { label: "Pricing", href: "#" },
  { label: "Login", href: "#" },
];

const defaultPrimaryAction: HeaderAction = {
  label: "Sign up",
  href: "#",
};

const BrandGlyph = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-[#EBDCCF]"
  >
    <path
      d="M2.32031 6.96493C2.32031 3.86852 3.86852 2.32031 6.96493 2.32031H16.2542C19.3506 2.32031 20.8988 3.86852 20.8988 6.96493V16.2542C20.8988 19.3506 19.3506 20.8988 16.2542 20.8988H6.96493C3.86852 20.8988 2.32031 19.3506 2.32031 16.2542"
      stroke="currentColor"
      strokeWidth="1.81501"
      strokeLinecap="round"
    />
    <path
      d="M2.32031 16.2547V11.6094"
      stroke="currentColor"
      strokeWidth="1.81501"
      strokeLinecap="round"
    />
    <path
      d="M6.96781 8.35749C7.73747 8.35749 8.3614 7.73356 8.3614 6.9639C8.3614 6.19424 7.73747 5.57031 6.96781 5.57031C6.19815 5.57031 5.57422 6.19424 5.57422 6.9639C5.57422 7.73356 6.19815 8.35749 6.96781 8.35749Z"
      fill="currentColor"
    />
    <path
      d="M11.6123 8.35749C12.382 8.35749 13.0059 7.73356 13.0059 6.9639C13.0059 6.19424 12.382 5.57031 11.6123 5.57031C10.8427 5.57031 10.2188 6.19424 10.2188 6.9639C10.2188 7.73356 10.8427 8.35749 11.6123 8.35749Z"
      fill="currentColor"
    />
    <path
      d="M16.2569 8.35749C17.0265 8.35749 17.6505 7.73356 17.6505 6.9639C17.6505 6.19424 17.0265 5.57031 16.2569 5.57031C15.4872 5.57031 14.8633 6.19424 14.8633 6.9639C14.8633 7.73356 15.4872 8.35749 16.2569 8.35749Z"
      fill="currentColor"
    />
  </svg>
);

export function NavigationHeader({
  brandLabel = "Skene",
  navItems = defaultNavItems,
  primaryAction = defaultPrimaryAction,
  secondaryAction,
  className,
}: NavigationHeaderProps) {
  return (
    <header
      className={cn(
        "relative isolate w-full border-b border-white/10 bg-gradient-to-b from-[#0a0908] via-[#090806] to-[#0a0908] text-[#f3d9be] shadow-[0_1px_0_rgba(255,255,255,0.08)_inset]",
        "before:pointer-events-none before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_50%_110%,rgba(237,194,156,0.12),transparent_45%)] before:opacity-70 before:content-['']",
        "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.7),transparent_50%)] after:opacity-70 after:content-['']",
        className,
      )}
    >
      <div className="relative mx-auto flex max-w-6xl items-center gap-10 px-6 py-4">
        <a href="#" className="flex items-center gap-3 text-[#EBDCCF]">
          <BrandGlyph />
          <span className="text-base font-semibold leading-none tracking-tight">
            {brandLabel}
          </span>
        </a>

        <div className="flex flex-1 items-center justify-end gap-8 text-sm font-medium">
          <nav className="flex items-center gap-7">
            {navItems.map((item) =>
              item.href ? (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={item.onClick}
                  className={cn(
                    "transition-colors",
                    item.active ? "text-white" : "text-[#f3d9be]",
                    "hover:text-white",
                  )}
                  aria-current={item.active ? "page" : undefined}
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.onClick}
                  className={cn(
                    "cursor-pointer bg-transparent transition-colors",
                    item.active ? "text-white" : "text-[#f3d9be]",
                    "hover:text-white",
                  )}
                >
                  {item.label}
                </button>
              ),
            )}
          </nav>

          <div className="flex items-center gap-3">
            {secondaryAction &&
              (secondaryAction.href ? (
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="text-[#f3d9be] hover:bg-white/5"
                >
                  <a href={secondaryAction.href}>{secondaryAction.label}</a>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#f3d9be] hover:bg-white/5"
                  onClick={secondaryAction.onClick}
                >
                  {secondaryAction.label}
                </Button>
              ))}

            {primaryAction &&
              (primaryAction.href ? (
                <Button
                  asChild
                  size="sm"
                  className="rounded-xl bg-[#edc29c] px-4 font-semibold text-[#1b140f] shadow-[0_10px_30px_-12px_rgba(237,194,156,0.6)] transition-colors hover:bg-[#f1d9c0]"
                >
                  <a href={primaryAction.href}>{primaryAction.label}</a>
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="rounded-xl bg-[#edc29c] px-4 font-semibold text-[#1b140f] shadow-[0_10px_30px_-12px_rgba(237,194,156,0.6)] transition-colors hover:bg-[#f1d9c0]"
                  onClick={primaryAction.onClick}
                >
                  {primaryAction.label}
                </Button>
              ))}
          </div>
        </div>
      </div>
    </header>
  );
}
